import { fetchEventSource } from '@microsoft/fetch-event-source';

import { useAuthStore } from '#ui-shared/stores/auth-store';
import { useConfigStore } from '#ui-shared/stores/config-store';

import type { SseHandlers, SseSource } from './sse-source';

/**
 * Builds the SSE listen path; shared with the webview simulator's native layer.
 */
export function buildSseListenPath(deviceId: string | null | undefined): string {
  if (!deviceId) {
    return '/api/nora/event/listen';
  }

  return `/api/nora/event/listen?deviceId=${encodeURIComponent(deviceId)}`;
}

export class FetchSseSource implements SseSource {
  private controller?: AbortController;

  constructor(private readonly deviceId: string | null) {}

  start(handlers: SseHandlers): void {
    if (this.controller) {
      return;
    }

    const config = useConfigStore();
    const authStore = useAuthStore();

    this.controller = new AbortController();

    const deviceId: string | null = this.deviceId;

    if (!deviceId) {
      console.warn('No device ID provided for SSE source, listening without a deviceId filter');
    }

    const url = `${config.baseUrl}${buildSseListenPath(deviceId)}`;

    fetchEventSource(url, {
      signal: this.controller.signal,
      headers: { Authorization: `Bearer ${authStore.token ?? ''}` },
      openWhenHidden: true,
      onopen: async (response) => {
        if (!response.ok) {
          throw new Error(`SSE open failed: ${response.status}`);
        }

        handlers.onopen?.();
      },
      onmessage: (event) => {
        handlers.onmessage(event.data);
      },
      onerror: (error) => {
        handlers.onerror?.(error);
      },
    });
  }

  stop(): void {
    this.controller?.abort();
    this.controller = undefined;
  }
}
